// v3.15-1202 COMP-010 — Employee Opening Balances (admin).
//
// Administrative baseline earnings for employees transitioning into the Hub.
// Never generates Compensation Events / Ledger entries / Payroll Transactions.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Landmark, Plus, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date((iso.length === 10 ? iso : iso.slice(0, 10)) + 'T00:00:00')
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'active'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span data-testid={`ob-status-${s}`}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
      {s}
    </span>
  );
};

const OBModal = ({ open, onClose, onSaved, existingOB, employees }) => {
  const [employeeId, setEmployeeId] = useState(existingOB?.employee_id || '');
  const [ytd, setYtd]               = useState(existingOB?.opening_ytd_earnings ?? '');
  const [lifetime, setLifetime]     = useState(existingOB?.opening_lifetime_earnings ?? '');
  const [effective, setEffective]   = useState(existingOB?.effective_date || new Date().toISOString().slice(0, 10));
  const [notes, setNotes]           = useState(existingOB?.notes || '');
  const [saving, setSaving]         = useState(false);

  const isSupersede = Boolean(existingOB?.id);

  const submit = async () => {
    if (!isSupersede && !employeeId) { toast.error('Employee is required'); return; }
    setSaving(true);
    try {
      const body = {
        opening_ytd_earnings:      Number(ytd || 0),
        opening_lifetime_earnings: Number(lifetime || 0),
        effective_date:            effective,
        notes:                     notes || undefined,
      };
      if (isSupersede) {
        const { data } = await api.post(`/opening-balances/${existingOB.id}/supersede`, body);
        toast.success(`Superseded — new active row ${data.item.id.slice(0, 8)}…`);
      } else {
        const { data } = await api.post('/opening-balances', { ...body, employee_id: employeeId });
        toast.success(`Opening Balance created for ${data.item.employee_name}`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail || 'Save failed';
      if (d === 'active_opening_balance_exists_for_effective_date') {
        toast.error('An active Opening Balance already exists for that date — use Supersede.');
      } else {
        toast.error(d);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-1">
            {isSupersede ? 'Supersede Opening Balance' : 'New Opening Balance'}
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Baseline earnings brought in from outside the Hub. Never creates
            payroll obligations, Compensation Events, ledger entries, or
            payroll transactions.
          </p>
          {!isSupersede && (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Employee</label>
              <select
                data-testid="ob-employee-select"
                value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— Select employee —</option>
                {(employees || []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.email} {u.role ? `· ${u.role}` : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Opening YTD Earnings</label>
          <Input data-testid="ob-ytd" type="number" step="0.01" value={ytd} onChange={(e) => setYtd(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Opening Lifetime Earnings</label>
          <Input data-testid="ob-lifetime" type="number" step="0.01" value={lifetime} onChange={(e) => setLifetime(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="ob-effective" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="ob-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional context" />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" data-testid="ob-cancel-btn" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="ob-save-btn" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isSupersede ? 'Supersede' : 'Create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function EmployeeOpeningBalancesPage() {
  const [loading, setLoading]     = useState(true);
  const [items, setItems]         = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [error, setError]         = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [obRes, uRes] = await Promise.all([
        api.get('/opening-balances', { params: { limit: 500 } }),
        api.get('/users', { params: { limit: 500 } }).catch(() => ({ data: [] })),
      ]);
      setItems(obRes?.data?.items || []);
      const users = Array.isArray(uRes?.data) ? uRes.data : (uRes?.data?.items || []);
      setEmployees(users);
    } catch (e) {
      if (e?.response?.status === 403) setError('Opening Balances administration is restricted to admins/directors.');
      else toast.error('Failed to load opening balances');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const active = useMemo(() => items.filter((r) => r.status === 'active'), [items]);
  const superseded = useMemo(() => items.filter((r) => r.status === 'superseded'), [items]);

  return (
    <div className="space-y-4" data-testid="opening-balances-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Landmark className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Employee Opening Balances</h1>
            <p className="text-sm text-slate-500">
              Administrative baseline earnings brought into the Hub. Never creates
              Compensation Events, Ledger entries, or Payroll Transactions.
            </p>
          </div>
        </div>
        <Button
          data-testid="ob-new-btn"
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Opening Balance
        </Button>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl"
             data-testid="ob-forbidden">{error}</div>
      ) : loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-800">Active Opening Balances</h2>
              <span className="text-xs text-slate-500">{active.length} record(s)</span>
            </div>
            {active.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="ob-active-empty">
                No active opening balances have been recorded yet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="ob-active-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Effective</th>
                    <th className="text-right px-4 py-2">Opening YTD</th>
                    <th className="text-right px-4 py-2">Opening Lifetime</th>
                    <th className="text-left px-4 py-2">Notes</th>
                    <th className="text-center px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((r) => (
                    <tr key={r.id} data-testid={`ob-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-semibold text-slate-800">{r.employee_name || r.employee_id}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.effective_date)}</td>
                      <td className="px-4 py-2 text-right font-semibold">{fmtMoney(r.opening_ytd_earnings)}</td>
                      <td className="px-4 py-2 text-right font-semibold">{fmtMoney(r.opening_lifetime_earnings)}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{r.notes || '—'}</td>
                      <td className="px-4 py-2 text-center"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-2 text-right">
                        <button
                          data-testid={`ob-supersede-${r.id}`}
                          onClick={() => { setEditing(r); setShowModal(true); }}
                          className="text-xs font-semibold text-blue-600 hover:underline">
                          Supersede
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {superseded.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                <History className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-800">Audit History (Superseded)</h2>
                <span className="ml-auto text-xs text-slate-500">{superseded.length} record(s)</span>
              </div>
              <table className="w-full text-sm" data-testid="ob-history-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Effective</th>
                    <th className="text-right px-4 py-2">Opening YTD</th>
                    <th className="text-right px-4 py-2">Opening Lifetime</th>
                    <th className="text-left px-4 py-2">Superseded By</th>
                    <th className="text-left px-4 py-2">Superseded At</th>
                  </tr>
                </thead>
                <tbody>
                  {superseded.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-4 py-2">{r.employee_name || r.employee_id}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.effective_date)}</td>
                      <td className="px-4 py-2 text-right">{fmtMoney(r.opening_ytd_earnings)}</td>
                      <td className="px-4 py-2 text-right">{fmtMoney(r.opening_lifetime_earnings)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{(r.superseded_by || '').slice(0, 8)}…</td>
                      <td className="px-4 py-2 text-xs">{fmtDate(r.superseded_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <OBModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSaved={fetchAll}
        existingOB={editing}
        employees={employees}
      />
    </div>
  );
}
